/**
 * Cautious Activity Bot strategy export/import (config recipe only).
 * Never includes runtime state, admin ownership, or KuCoin config ids.
 */

import type {
  CryptoPortfolioActivityBot,
  TradingFilterProfile,
  UpdateActivityBotAiAutoTuneRequest,
} from '../services/cryptoPortfolioService';

export const ACTIVITY_BOT_EXPORT_TYPE = 'CRYPTO_PORTFOLIO_ACTIVITY_BOT';
export const ACTIVITY_BOT_EXPORT_SCHEMA_VERSION = 1;

export interface ActivityBotExportBot {
  referenceName?: string | null;
  sectorId: string;
  amountUsd: number;
  leverage?: number;
  totalPositions: number;
  longCount: number;
  shortCount: number;
  targetProfitPct: number;
  stopLossPct?: number | null;
  trailingStopEnabled?: boolean;
  trailingStopActivationPct?: number | null;
  trailingStopDistancePct?: number | null;
  maxBotLossUsd?: number | null;
  maxBotLossPct?: number | null;
  continuousRun?: boolean;
  entryMode?: string;
  /** Prefer resolving by name on import; id is profile-local and ignored. */
  filterProfileName?: string | null;
  macroBtcBridgeEnabled?: boolean;
  macroCommodityBridgeEnabled?: boolean;
  /**
   * Optional AI tune settings. On import these are applied with auto-tune forced OFF
   * so importing never starts live tuning on another profile.
   */
  aiTune?: UpdateActivityBotAiAutoTuneRequest | null;
  /** Hint only — never used as kucoinConfigId on import. */
  sourceKucoinAccountName?: string | null;
}

export interface ActivityBotExportFile {
  schemaVersion: number;
  type: string;
  exportedAt: string;
  bot: ActivityBotExportBot;
}

export interface ActivityBotImportPrefill {
  referenceName: string;
  sectorId: string;
  amountUsd: string;
  leverage: string;
  totalPositions: string;
  longCount: string;
  shortCount: string;
  targetProfitPct: string;
  stopLossPct: string;
  trailingStopEnabled: boolean;
  trailActivationPct: string;
  trailDistancePct: string;
  maxBotLossUsd: string;
  maxBotLossPct: string;
  continuousRun: boolean;
  entryMode: 'ACTIVITY_ONLY' | 'PEAK_TROUGH_TIMING';
  filterProfileId: number | '';
  filterProfileWarning: string | null;
  macroBtcBridgeEnabled: boolean;
  macroCommodityBridgeEnabled: boolean;
  aiTuneToApply: UpdateActivityBotAiAutoTuneRequest | null;
  importNotes: string[];
}

function numOrNull(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function strOrEmpty(v: unknown): string {
  if (v == null) return '';
  return String(v);
}

export function buildActivityBotExportPayload(bot: CryptoPortfolioActivityBot): ActivityBotExportFile {
  const aiTune: UpdateActivityBotAiAutoTuneRequest = {
    aiAutoTuneEnabled: false,
    aiAutoTuneRequireApproval: bot.aiAutoTuneRequireApproval,
    aiPerformanceTuneEnabled: bot.aiPerformanceTuneEnabled,
    aiPerformanceTuneWindow: bot.aiPerformanceTuneWindow,
    aiPerformanceTuneMinTrades: bot.aiPerformanceTuneMinTrades,
    aiPerformanceSideBlockEnabled: bot.aiPerformanceSideBlockEnabled,
    aiPerformanceTuneLlmEnabled: bot.aiPerformanceTuneLlmEnabled,
    aiPerformanceTuneHistoryLearningEnabled: bot.aiPerformanceTuneHistoryLearningEnabled,
    lowParticipationMode: bot.lowParticipationMode ?? undefined,
    lowParticipationAutoBlockDays: bot.lowParticipationAutoBlockDays ?? undefined,
    aiRegimeLongBlockEnabled: bot.aiRegimeLongBlockEnabled,
    aiRegimeLongBlockList: bot.aiRegimeLongBlockList ?? undefined,
    scalperModeEnabled: bot.scalperModeEnabled,
  };

  const hadAiFeatures = Boolean(
    bot.aiAutoTuneEnabled
      || bot.aiPerformanceTuneEnabled
      || bot.scalperModeEnabled
      || bot.aiRegimeLongBlockEnabled
      || bot.lowParticipationMode
  );

  return {
    schemaVersion: ACTIVITY_BOT_EXPORT_SCHEMA_VERSION,
    type: ACTIVITY_BOT_EXPORT_TYPE,
    exportedAt: new Date().toISOString(),
    bot: {
      referenceName: bot.referenceName ?? bot.displayLabel ?? null,
      sectorId: bot.sectorId,
      amountUsd: Number(bot.amountUsd),
      leverage: bot.leverage != null ? Number(bot.leverage) : 5,
      totalPositions: Number(bot.totalPositions),
      longCount: Number(bot.longCount),
      shortCount: Number(bot.shortCount),
      targetProfitPct: Number(bot.targetProfitPct),
      stopLossPct: bot.stopLossPct != null ? Number(bot.stopLossPct) : null,
      trailingStopEnabled: Boolean(bot.trailingStopEnabled),
      trailingStopActivationPct:
        bot.trailingStopActivationPct != null ? Number(bot.trailingStopActivationPct) : null,
      trailingStopDistancePct:
        bot.trailingStopDistancePct != null ? Number(bot.trailingStopDistancePct) : null,
      maxBotLossUsd: bot.maxBotLossUsd != null ? Number(bot.maxBotLossUsd) : null,
      maxBotLossPct: bot.maxBotLossPct != null ? Number(bot.maxBotLossPct) : null,
      continuousRun: Boolean(bot.continuousRun),
      entryMode: bot.entryMode === 'PEAK_TROUGH_TIMING' ? 'PEAK_TROUGH_TIMING' : 'ACTIVITY_ONLY',
      filterProfileName: bot.filterProfileName ?? null,
      macroBtcBridgeEnabled: Boolean(bot.macroBtcBridgeEnabled),
      macroCommodityBridgeEnabled: Boolean(bot.macroCommodityBridgeEnabled),
      aiTune: hadAiFeatures ? aiTune : null,
      sourceKucoinAccountName: bot.kucoinAccountName ?? null,
    },
  };
}

export function downloadActivityBotExport(bot: CryptoPortfolioActivityBot): void {
  const payload = buildActivityBotExportPayload(bot);
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safeName = (bot.referenceName || bot.displayLabel || `bot-${bot.id}`)
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || `bot-${bot.id}`;
  a.href = url;
  a.download = `activity-bot-${safeName}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function parseActivityBotExportJson(raw: string): ActivityBotExportFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('Invalid JSON file');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('Export file must be a JSON object');
  }
  const file = parsed as Partial<ActivityBotExportFile>;
  if (file.type !== ACTIVITY_BOT_EXPORT_TYPE) {
    throw new Error('Not an activity bot export (unexpected type)');
  }
  if (file.schemaVersion == null || Number(file.schemaVersion) > ACTIVITY_BOT_EXPORT_SCHEMA_VERSION) {
    throw new Error(`Unsupported schemaVersion: ${String(file.schemaVersion)}`);
  }
  if (!file.bot || typeof file.bot !== 'object') {
    throw new Error('Export file missing bot config');
  }
  const bot = file.bot;
  if (!bot.sectorId || typeof bot.sectorId !== 'string') {
    throw new Error('bot.sectorId is required');
  }
  if (bot.amountUsd == null || !Number.isFinite(Number(bot.amountUsd)) || Number(bot.amountUsd) <= 0) {
    throw new Error('bot.amountUsd must be a positive number');
  }
  const total = Number(bot.totalPositions);
  const longs = Number(bot.longCount);
  const shorts = Number(bot.shortCount);
  if (!Number.isFinite(total) || total <= 0) {
    throw new Error('bot.totalPositions must be a positive number');
  }
  if (!Number.isFinite(longs) || !Number.isFinite(shorts) || longs + shorts !== total) {
    throw new Error('bot.longCount + bot.shortCount must equal totalPositions');
  }
  return {
    schemaVersion: Number(file.schemaVersion),
    type: ACTIVITY_BOT_EXPORT_TYPE,
    exportedAt: typeof file.exportedAt === 'string' ? file.exportedAt : new Date().toISOString(),
    bot: bot as ActivityBotExportBot,
  };
}

export function buildActivityBotImportPrefill(
  file: ActivityBotExportFile,
  filterProfiles: TradingFilterProfile[],
): ActivityBotImportPrefill {
  const bot = file.bot;
  const notes: string[] = [];
  notes.push('Created as DRAFT — review and pick a KuCoin account before Start.');
  notes.push('KuCoin account is not copied from the export (avoids wrong-account / size stacking).');

  let filterProfileId: number | '' = '';
  let filterProfileWarning: string | null = null;
  const wantedName = (bot.filterProfileName || '').trim();
  if (wantedName) {
    const match = filterProfiles.find(
      p => (p.name || '').trim().toLowerCase() === wantedName.toLowerCase(),
    );
    if (match) {
      filterProfileId = match.id;
    } else {
      filterProfileWarning =
        `Filter profile "${wantedName}" not found on this account — import continues without it.`;
      notes.push(filterProfileWarning);
    }
  }

  if (bot.sourceKucoinAccountName) {
    notes.push(`Source account hint: ${bot.sourceKucoinAccountName} (select the target account below).`);
  }

  let aiTuneToApply: UpdateActivityBotAiAutoTuneRequest | null = null;
  if (bot.aiTune && typeof bot.aiTune === 'object') {
    aiTuneToApply = {
      ...bot.aiTune,
      // Never enable live auto-tune on import
      aiAutoTuneEnabled: false,
    };
    notes.push('AI tune settings will be applied with Auto-tune left OFF.');
  }

  const sectorId = String(bot.sectorId).trim().toUpperCase();
  const entryMode = bot.entryMode === 'PEAK_TROUGH_TIMING' ? 'PEAK_TROUGH_TIMING' : 'ACTIVITY_ONLY';
  const baseName = strOrEmpty(bot.referenceName).trim() || `${sectorId} imported`;

  return {
    referenceName: baseName.endsWith('(imported)') ? baseName : `${baseName} (imported)`,
    sectorId,
    amountUsd: String(Number(bot.amountUsd)),
    leverage: String(Math.min(10, Math.max(1, Math.round(Number(bot.leverage ?? 5)) || 5))),
    totalPositions: String(Number(bot.totalPositions)),
    longCount: String(Number(bot.longCount)),
    shortCount: String(Number(bot.shortCount)),
    targetProfitPct: String(Number(bot.targetProfitPct)),
    stopLossPct: bot.stopLossPct != null ? String(numOrNull(bot.stopLossPct) ?? '') : '',
    trailingStopEnabled: Boolean(bot.trailingStopEnabled),
    trailActivationPct:
      bot.trailingStopActivationPct != null
        ? String(numOrNull(bot.trailingStopActivationPct) ?? '5')
        : '5',
    trailDistancePct:
      bot.trailingStopDistancePct != null
        ? String(numOrNull(bot.trailingStopDistancePct) ?? '4')
        : '4',
    maxBotLossUsd: bot.maxBotLossUsd != null ? String(numOrNull(bot.maxBotLossUsd) ?? '') : '',
    maxBotLossPct: bot.maxBotLossPct != null ? String(numOrNull(bot.maxBotLossPct) ?? '') : '',
    continuousRun: Boolean(bot.continuousRun),
    entryMode,
    filterProfileId,
    filterProfileWarning,
    macroBtcBridgeEnabled: Boolean(bot.macroBtcBridgeEnabled) && sectorId !== 'COMMODITIES',
    macroCommodityBridgeEnabled: Boolean(bot.macroCommodityBridgeEnabled) && sectorId === 'COMMODITIES',
    aiTuneToApply,
    importNotes: notes,
  };
}
